import { Link } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Button from "react-bootstrap/esm/Button"
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import TreeView from 'react-accessible-treeview'

import Icon from './Icon'
import { iconForOrgType } from "../utils/utils"

function BusinessEntityTree({ businessEntitiesSignal, currentBusinessEntityId, currentUrlFragment, rootBusinessEntityId, permissionMasks, showTreeSignal, handleToggleTree }) {

    const filterSignal = useSignal('')
    // const businessEntitiesSignal = useSignal([])
    const expandAllSignal = useSignal(false)
    const treeKey = useComputed(() => {
        // this ensures we likely get a unique key each time the tree data changes
        // the purpose of this is to force the tree to rebuild each render to work around
        // an issue in the treeview when you use both selectedid and expandedid props
        return JSON.stringify(treeData.value).length
    })

    const treeData = useComputed(() => {
        const lowerCaseFilter = filterSignal.value.toLowerCase()

        if (!lowerCaseFilter.length) {
            // short-circuit and return all
            return transformEntitiesToTree([...businessEntitiesSignal.value])
        }

        // make copy of businessEntities
        let data = [...businessEntitiesSignal.value]

        // filter those businessEntities based on filterSignal
        data = data.filter(be => be.name.toLowerCase().includes(lowerCaseFilter))
        console.log(data)

        // add back in parents to root or highest org we have in the data so the UI makes sense
        data.forEach(item => {
            let current = item
            while(current && current.parentId && current.parentId != 0) {
                let parent = businessEntitiesSignal.value.find(be => be.id == current.parentId)
                if (parent && data.findIndex(be => be.id == parent.id) == -1) {
                    data.push(parent)
                }

                current = parent
            }
        })

        // return modified data for the tree control
        const result = transformEntitiesToTree(data)
        //console.log('returning transformed')
        //console.log(result)
        return result
    })

    const expandedIds = useComputed(() => {
        if(!expandAllSignal.value) {
            let defaultExpandedIds = []

            // Expand the currently selected business entity and its parents
            let current = treeData.value.find(item => item.id == currentBusinessEntityId)
            if (current) {
                defaultExpandedIds.push(current.id)
            }
            while(current && current.parent && current.parent != 0) {
                let parent = treeData.value.find(be => be.id == current.parent)
                if (parent && defaultExpandedIds.indexOf(parent.id) == -1) {
                    defaultExpandedIds.push(parent.id)
                }

                current = parent
            }

            return defaultExpandedIds
        }
        const expandedItems = treeData.value.filter(item => item.children.length)
        const expandedItemIds = expandedItems.map(item => item.id)
        
        const uniqueSet = new Set(expandedItemIds)
        const unique = [...uniqueSet]
        return unique
    })

    const selectedIds = useComputed(() => {
        return [`${currentBusinessEntityId}`]
    })

    function transformEntitiesToTree(data) {
        const entities = Array.isArray(data) ? data : []

        if (entities.length) {

            // Build a lookup table keyed by id.
            const nodes = {};

            // Create a virtual root node with id 0.
            nodes[0] = { id: 0, name: "", parent: null, children: [rootBusinessEntityId] };

            // Create a node for each entity, always including a children array.
            // NOTE: the old site had an option for merchants that used a user icon. However, don't see any active organizations of that type

            // If the user's root is somewhere in the tree, then that node's parent will point to a non-existent node.
            // Adjust this so the specified parentid points to the empty root node

            entities.forEach(entity => {
                nodes[entity.id] = {
                    id: entity.id,
                    name: entity.name,
                    parent: entity.id == rootBusinessEntityId ? 0 : entity.parentId,
                    children: [], // Always include children, even if empty.
                    metadata: { ...entity, icon: iconForOrgType(entity), isCurrentSelected: entity.id == currentBusinessEntityId }
                };
            });

            // Attach nodes as children of their parent.
            Object.values(nodes).forEach(node => {
                // Skip the virtual root (or any node with a null parent).
                if (node.parent !== null && nodes[node.parent] && !nodes[node.parent].children.includes(node.id)) {
                    nodes[node.parent].children.push(node.id);
                }
            });

            // Sort each parent's children array alphabetically by child name.
            Object.values(nodes).forEach(node => {
                if (node.children.length > 0) {
                    node.children.sort((a, b) =>
                        nodes[a].name.localeCompare(nodes[b].name)
                    );
                }
            });

            // Sort array alphabetically by name
            const result = Object.values(nodes).sort((a, b) =>
                a.name.localeCompare(b.name)
            );

            return result;

        }
        return entities
    }

    const getCaretIcon = (element, isExpanded) => {
        if (element.children && element.children.length > 0) {
            // caret
            if (isExpanded) {
                return 'caret-right-fill'
            }
            return 'caret-down-fill'
        }
        return 'blank'
    }

    const handleFilter = (e) => {
        filterSignal.value = e.target.value
        expandAllSignal.value = true
    }

    const getLink = id => {
        // make links smart so that if the BE changes, the user stays on the same list page
        return `/be/${id}/${currentUrlFragment}`
    }

    if (!showTreeSignal.value) {
        return (
            <Button size="sm" variant="outline-secondary" className="align-items-center justify-content-center mx-2 mt-3" onClick={() => handleToggleTree(true)}><Icon icon='arrows-expand-vertical' width="16" height="16" ></Icon></Button>
 
        )
    }

    return (
        <>
            <div className="align-items-center mx-1 mt-3">
                <Button size="sm" variant="outline-secondary" className="align-items-center justify-content-center mx-1" onClick={() => handleToggleTree(!showTreeSignal.value)}>
                    <Icon icon='arrows-expand-vertical' width="16" height="16" ></Icon>
                </Button>
                <h3 className="display-1 fs-2 px-3 my-2">Accounts</h3>
            </div>
            <InputGroup className="px-2 mb-1">
                <Form.Control aria-label="Business Entity Filter" aria-describedby="be-filter" onChange={e => handleFilter(e)} />
                <InputGroup.Text id="be-filter" value={filterSignal.value}>
                    <Icon icon="search" width="16" height="16"></Icon>
                </InputGroup.Text>
            </InputGroup>
            <InputGroup className="px-2">
                <Form.Check // prettier-ignore
                    type="switch"
                    id="custom-switch"
                    label="Expand All"
                    onChange={(e) => expandAllSignal.value = !!e.target.checked}
                />
            </InputGroup>
            {(treeData.value && treeData.value.length > 1) && (
                <TreeView
                    key={treeKey.value}
                    data={treeData.value}
                    className="be-treeview"
                    expandedIds={expandedIds.value}
                    selectedIds={selectedIds.value}
                    nodeRenderer={({
                        element,
                        getNodeProps,
                        isSelected,
                        isExpanded,
                        level,
                        handleSelect
                    }) => {
                        return (
                            // jsx
                            <div {...getNodeProps()} style={{ paddingLeft: 20 * (level - 1) }} className={`d-block text-truncate text-nowrap`}>
                                <Icon icon={getCaretIcon(element, isExpanded)} width="16" height="16"></Icon>
                                <span className="ml-1">&nbsp;</span>
                                <Icon icon={element.metadata.icon}></Icon>
                                <span className={`mx-1 px-1 ${element.metadata.id == currentBusinessEntityId ? 'bg-primary-subtle text-black' : ''}`}>
                                    <Link to={getLink(element.id)}>{element.name} </Link>
                                </span>
                            </div>
                        )
                    }
                    }
                ></TreeView>
            )}
        </>
    )
}

export default BusinessEntityTree